import * as React from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import { saveBlob } from '@/lib/utils/download';
import {
  fetchIntakeDocumentContent,
  useCandidateSourceQuery,
  useDocumentPageQuery,
} from '@/lib/api/requirement-library-intake';
import { plural } from '../library-logic';
import { citationLocation, locationWords, NO_PAGES_NOTE, splitAtRange } from './intake-logic';

/**
 * Source viewer for an AI-extracted requirement (T-0154; E11-F08
 * scenario 6, the citation round trip).
 *
 * Shows the page the passage came from with the cited span highlighted
 * (the citation highlight token), the other passages when the document
 * states the requirement more than once (scenario 11), the pages before
 * and after, and a link to download the original file.
 *
 * The same viewer opens from a candidate in the review and from a
 * requirement already in a draft or published, whose `source_ref` names
 * the run and the candidate.
 *
 * A withdrawn document keeps the quoted text and nothing else: the viewer
 * shows the quote and says the source was withdrawn.
 *
 * Where the passage is (T-0155) follows the citation's location: a page
 * ("Page 7 of 38 · 3.2 Accounts Payable"), a sheet and row of a workbook
 * (previous / next move between sheets), or, for a Word file that records
 * no pages, the section and paragraph with a note that the file has no
 * pages; there is then no page to move to.
 */

export interface SourceTarget {
  runId: string;
  candidateId: string;
  /** Which citation to open on, from 0. */
  citationIndex?: number;
}

export interface IntakeSourceViewerProps {
  target: SourceTarget | null;
  onClose: () => void;
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function IntakeSourceViewer({ target, onClose }: IntakeSourceViewerProps) {
  const sourceQuery = useCandidateSourceQuery(target?.runId ?? null, target?.candidateId ?? null);
  const source = sourceQuery.data;

  const [citationIndex, setCitationIndex] = React.useState(0);
  /** A page other than the cited one, reached with previous / next. */
  const [browsePage, setBrowsePage] = React.useState<number | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const markRef = React.useRef<HTMLElement>(null);

  const targetKey = target ? `${target.runId}:${target.candidateId}` : '';
  React.useEffect(() => {
    setCitationIndex(target?.citationIndex ?? 0);
    setBrowsePage(null);
    setDownloadError(null);
    // Reset when another source is opened, not on every render of `target`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, target?.citationIndex]);

  const citations = source?.citations ?? [];
  const citation = citations[Math.min(citationIndex, Math.max(citations.length - 1, 0))] ?? null;
  const citedPage = citation?.page_number ?? null;
  const withdrawn = source?.withdrawn === true;
  const pageCount = source?.document.page_count ?? null;
  const pageSource = source?.document.page_source ?? null;
  const location = citation ? citationLocation(citation, pageSource) : null;
  const sheets = location?.kind === 'sheet_row';
  const noPages = location?.kind === 'section';
  const unit = sheets ? 'sheet' : 'page';
  const shownPage = browsePage ?? citedPage;
  const browsing = browsePage != null && browsePage !== citedPage;

  const pageQuery = useDocumentPageQuery(
    browsing && source && !withdrawn ? source.document.id : null,
    browsing ? browsePage : null,
  );

  // Bring the highlighted passage into view when it is rendered.
  React.useEffect(() => {
    if (!browsing) markRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [browsing, citationIndex, source]);

  const canPrevious = !withdrawn && !noPages && shownPage != null && shownPage > 1;
  const canNext =
    !withdrawn && !noPages && shownPage != null && (pageCount == null || shownPage < pageCount);

  /** The line above the text: where it is, in a reader's terms. */
  let whereLine = '';
  if (location && !browsing) {
    if (location.kind === 'page') {
      whereLine = `Page ${location.page}${pageCount ? ` of ${pageCount}` : ''}${
        location.section ? ` · ${location.section}` : ''
      }`;
    } else if (location.kind === 'sheet_row') {
      whereLine = `Sheet ${location.sheet}${location.row != null ? ` · row ${location.row}` : ''}${
        pageCount && pageCount > 1 ? ` (sheet ${citedPage} of ${pageCount})` : ''
      }`;
    } else {
      whereLine = locationWords(location);
    }
  } else if (browsing) {
    const section = pageQuery.data?.section_path ?? null;
    whereLine = sheets
      ? `Sheet ${section ?? browsePage}${pageCount ? ` (sheet ${browsePage} of ${pageCount})` : ''}`
      : `Page ${browsePage}${pageCount ? ` of ${pageCount}` : ''}${section ? ` · ${section}` : ''}`;
  }

  const download = () => {
    if (!source) return;
    setDownloading(true);
    setDownloadError(null);
    fetchIntakeDocumentContent(source.document).then(
      ({ blob, fileName }) => {
        setDownloading(false);
        saveBlob(fileName, blob);
      },
      (err: unknown) => {
        setDownloading(false);
        setDownloadError(
          err instanceof ApiError && err.details?.['reason'] === 'document_withdrawn'
            ? 'This document was withdrawn and can no longer be downloaded.'
            : messageOf(err, 'The file could not be downloaded.'),
        );
      },
    );
  };

  let body: React.ReactNode;
  if (sourceQuery.isPending) {
    body = (
      <div role="status" className="flex items-center gap-2 py-6 text-body-sm text-ink-500">
        <Spinner /> Loading the source…
      </div>
    );
  } else if (sourceQuery.isError || !source) {
    body = (
      <WorkspaceNotice tone="critical">
        The source could not be loaded. {messageOf(sourceQuery.error, '')}{' '}
        <button
          type="button"
          className="font-medium underline underline-offset-[3px]"
          onClick={() => void sourceQuery.refetch()}
        >
          Try again
        </button>
      </WorkspaceNotice>
    );
  } else if (!citation) {
    body = <WorkspaceNotice>This requirement has no recorded passage.</WorkspaceNotice>;
  } else {
    const parts =
      !browsing && citation.page_text != null
        ? splitAtRange(citation.page_text, citation.start, citation.end)
        : null;
    body = (
      <>
        {withdrawn ? (
          <WorkspaceNotice tone="at-risk">
            <strong className="font-semibold text-ink-900">Source withdrawn.</strong> A firm admin
            withdrew this document, so its pages can no longer be opened. The quoted passage is kept
            with the requirement.
          </WorkspaceNotice>
        ) : null}

        {citations.length > 1 ? (
          <div
            role="group"
            aria-label="Passages"
            className="flex flex-wrap items-center gap-1.5 text-meta text-ink-500"
          >
            Stated {plural(citations.length, 'time')} in this document:
            {citations.map((c, i) => (
              <button
                key={`${c.page_number}:${c.start}`}
                type="button"
                aria-pressed={!browsing && i === citationIndex}
                onClick={() => {
                  setCitationIndex(i);
                  setBrowsePage(null);
                }}
                className={
                  !browsing && i === citationIndex
                    ? 'inline-flex h-7 items-center rounded-full border border-ink-900 bg-ink-900 px-2.5 font-medium text-paper-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1'
                    : 'inline-flex h-7 items-center rounded-full border border-ink-200 bg-paper-0 px-2.5 font-medium text-ink-700 hover:border-ink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1'
                }
              >
                Passage {i + 1} · {locationWords(citationLocation(c, pageSource))}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <span
            data-testid="source-location"
            title={whereLine}
            className="min-w-0 flex-1 truncate text-meta font-medium text-ink-700"
          >
            {whereLine}
          </span>
          {browsing ? (
            <WorkspaceButton variant="tertiary" onClick={() => setBrowsePage(null)}>
              Back to the cited passage
            </WorkspaceButton>
          ) : null}
          {noPages ? null : (
            <>
              <WorkspaceButton
                aria-label={`Previous ${unit}`}
                disabled={!canPrevious}
                onClick={() => shownPage != null && setBrowsePage(shownPage - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Previous
              </WorkspaceButton>
              <WorkspaceButton
                aria-label={`Next ${unit}`}
                disabled={!canNext}
                onClick={() => shownPage != null && setBrowsePage(shownPage + 1)}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              </WorkspaceButton>
            </>
          )}
        </div>
        {noPages ? (
          <p data-testid="no-pages-note" className="text-meta text-ink-500">
            {NO_PAGES_NOTE}
          </p>
        ) : null}

        <div
          data-testid="source-page"
          className="max-h-[46vh] min-h-[160px] overflow-y-auto whitespace-pre-wrap rounded-card border border-ink-100 bg-paper-50 px-4 py-3 text-body-sm leading-6 text-ink-700"
        >
          {browsing ? (
            pageQuery.isPending ? (
              <span role="status" className="flex items-center gap-2 text-ink-500">
                <Spinner /> Loading {unit} {browsePage}…
              </span>
            ) : pageQuery.isError ? (
              <span className="text-ink-500">
                {pageQuery.error instanceof ApiError && pageQuery.error.code === 'not_found'
                  ? `This document has no ${unit} ${browsePage}.`
                  : `This ${unit} could not be loaded.`}
              </span>
            ) : (
              (pageQuery.data.text ?? `This ${unit} is no longer available.`)
            )
          ) : parts ? (
            <>
              {parts.before}
              <mark
                ref={markRef}
                data-testid="cited-span"
                className="rounded-[2px] bg-cite-highlight px-px text-ink-900"
              >
                {parts.cited}
              </mark>
              {parts.after}
            </>
          ) : (
            <mark
              data-testid="cited-span"
              className="rounded-[2px] bg-cite-highlight px-px text-ink-900"
            >
              {citation.quote}
            </mark>
          )}
        </div>
        {downloadError ? <WorkspaceNotice tone="critical">{downloadError}</WorkspaceNotice> : null}
      </>
    );
  }

  return (
    <WorkspaceDialog
      open={target != null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={source ? source.document.file_name : 'Source document'}
      description={
        source ? (
          <span className="flex flex-wrap items-center gap-2">
            Extracted by AI from this document
            <AiConfidenceChip value={source.confidence} />
          </span>
        ) : undefined
      }
      widthClassName="w-[820px]"
      actions={
        <>
          {source && !withdrawn ? (
            <WorkspaceButton size="md" disabled={downloading} onClick={download}>
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              {downloading ? 'Downloading…' : 'Download the original file'}
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton size="md" variant="primary" onClick={onClose}>
            Close
          </WorkspaceButton>
        </>
      }
    >
      {body}
    </WorkspaceDialog>
  );
}
